import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

/**
 * Ga4Service — a thin, safe wrapper around the GA4 `gtag.js` that is already
 * loaded in `index.html`. Every call is guarded so analytics can never throw
 * or break the app, and it degrades to a no-op if gtag is absent (e.g. ad-block,
 * SSR, tests).
 *
 * IMPORTANT: this is 100% client-side. Events are sent straight to Google Analytics.
 * Nothing here touches our database or any server endpoint.
 */
@Injectable({ providedIn: 'root' })
export class Ga4Service {
  /** GA4 measurement id, sourced from the Angular environment (matches index.html's gtag.js). */
  private readonly measurementId = environment.ga4MeasurementId;

  private get gtag(): ((...args: any[]) => void) | undefined {
    return typeof window !== 'undefined' ? (window as any).gtag : undefined;
  }

  private event(name: string, params?: Record<string, any>): void {
    try {
      const g = this.gtag;
      if (typeof g === 'function') {
        g('event', name, params || {});
      }
    } catch {
      /* analytics must never break the app */
    }
  }

  /** SPA virtual page-view — call on every Angular route change (fixes the SPA gap). */
  public pageView(path: string, title?: string): void {
    this.event('page_view', {
      page_path: path,
      page_location: typeof window !== 'undefined' ? window.location.href : path,
      page_title: title,
    });
  }

  /** Stamp the logged-in user so GA4 stitches sessions/devices to a real person. */
  public setUser(userId?: number): void {
    try {
      const g = this.gtag;
      if (typeof g === 'function' && userId) {
        g('config', this.measurementId, { user_id: String(userId) });
      }
    } catch {
      /* no-op */
    }
  }

  /** Event 1 — search performed (top-of-funnel + zero-result demand). */
  public search(term: string, resultCount?: number, filters?: Record<string, any>): void {
    this.event('search', {
      search_term: term || '',
      result_count: resultCount ?? null,
      zero_results: (resultCount ?? 0) === 0,
      ...(filters || {}),
    });
  }

  /**
   * `bookingType` (`booking_type` in GA4) splits the funnel by lease type:
   * 'daily' (day/week bookings), 'seasonal' (in-listing seasonal rentals),
   * 'annual' (term-offer checkout). Kept consistent across
   * begin_checkout → add_payment_info → purchase within a flow so the funnel lines up.
   */

  /** Event 2a — checkout started. */
  public beginCheckout(listingId: number, value?: number, bookingType?: string): void {
    this.event('begin_checkout', {
      currency: 'USD',
      value: value ?? null,
      listing_id: listingId,
      booking_type: bookingType || null,
    });
  }

  /** Event 2b — payment step reached (the gap vs purchase = the abandonment). */
  public addPaymentInfo(listingId: number, value?: number, bookingType?: string): void {
    this.event('add_payment_info', {
      currency: 'USD',
      value: value ?? null,
      listing_id: listingId,
      booking_type: bookingType || null,
    });
  }

  /** Event 3 — availability check failed (a concrete "why" for drop-off). */
  public availabilityFailed(listingId: number, reason: string, bookingType?: string): void {
    this.event('availability_unavailable', {
      listing_id: listingId,
      reason: reason,
      booking_type: bookingType || null,
    });
  }

  /**
   * Standard ecommerce view_item — fire on listing-detail view (fixes the non-standard one).
   * `bookingConfig` carries the listing's booking-type display configuration (e.g. 'OnlyDaily',
   * 'SeasonalAndDaily') so listing views can be segmented by what the listing offers — a listing
   * view isn't a single booking_type, so this is a separate `listing_booking_config` dimension.
   */
  public viewItem(listingId: number, title?: string, bookingConfig?: string): void {
    this.event('view_item', {
      currency: 'USD',
      listing_booking_config: bookingConfig || null,
      items: [{ item_id: String(listingId), item_name: title || '' }],
    });
  }

  /** Standard login event (fire alongside setUser). */
  public login(method: string = 'b2c'): void {
    this.event('login', { method });
  }

  /** Standard sign_up event on account creation. */
  public signUp(method: string = 'b2c'): void {
    this.event('sign_up', { method });
  }

  /** Standard ecommerce purchase on a completed booking. */
  public purchase(transactionId: string, value?: number, listingId?: number, bookingType?: string): void {
    this.event('purchase', {
      transaction_id: transactionId,
      currency: 'USD',
      value: value ?? null,
      booking_type: bookingType || null,
      items: listingId ? [{ item_id: String(listingId) }] : [],
    });
  }

  /** Standard generate_lead — email capture / contact form / exit-intent. */
  public generateLead(source: string): void {
    this.event('generate_lead', { source });
  }

  /** Standard add_to_wishlist — listing favorited. */
  public addToWishlist(listingId: number): void {
    this.event('add_to_wishlist', { currency: 'USD', items: [{ item_id: String(listingId) }] });
  }

  /** Standard share — listing shared. */
  public share(listingId?: number): void {
    this.event('share', { content_type: 'listing', item_id: listingId ? String(listingId) : '' });
  }

  /** Availability check succeeded (complements availability_unavailable → yields a success rate). */
  public availabilityChecked(listingId: number, availableDays?: number, bookingType?: string): void {
    this.event('availability_checked', {
      listing_id: listingId,
      available_days: availableDays ?? null,
      booking_type: bookingType || null,
    });
  }

  /** Exit-intent popup triggered. */
  public exitIntent(): void {
    this.event('exit_intent', {});
  }

  /** Standard select_item — a search result was clicked. */
  public selectItem(listingId: number, title?: string, listName: string = 'search_results'): void {
    this.event('select_item', {
      item_list_name: listName,
      items: [{ item_id: String(listingId), item_name: title || '' }],
    });
  }
}
